export type OidcConfig = {
  baseUrl: string
  realm: string
  clientId: string
  redirectUri: string
  postLogoutRedirectUri: string
}

export const useAuth = () => {
  const config = useRuntimeConfig()
  const publicCfg = config.public as Record<string, string>

  const oidc: OidcConfig = {
    baseUrl: publicCfg.oidcBaseUrl || '',
    realm: publicCfg.oidcRealm || '',
    clientId: publicCfg.oidcClientId || '',
    redirectUri: publicCfg.oidcRedirectUri || '',
    postLogoutRedirectUri: publicCfg.oidcPostLogoutRedirectUri || '',
  }

  console.log(oidc)

  // Central reactive auth state
  const authToken = useState<string | null>('auth_token', () =>
    import.meta.client ? localStorage.getItem('auth_token') : null,
  )
  const refreshTokenState = useState<string | null>('refresh_token', () =>
    import.meta.client ? localStorage.getItem('refresh_token') : null,
  )
  const isAuthReady = useState<boolean>('auth_ready', () => false)
  const businessStatus = useState<string | null>('business_status', () => null)
  const userRoles = useState<string[]>('user_roles', () => [])
  
  const isAuthenticated = computed(() => !!authToken.value)
  const isAdmin = computed(() => userRoles.value.includes('admin'))

  const parseRoles = (token: string | null) => {
    if (!token) {
      userRoles.value = []
      return
    }
    try {
      const part = token.split('.')[1] || ''
      const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
      const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
      const payload = JSON.parse(atob(padded))
      
      const roles = new Set<string>()
      
      // Realm roles
      if (payload.realm_access?.roles) {
        payload.realm_access.roles.forEach((r: string) => roles.add(r))
      }
      
      // Resource roles (client roles)
      if (payload.resource_access) {
        for (const client in payload.resource_access) {
          if (payload.resource_access[client].roles) {
            payload.resource_access[client].roles.forEach((r: string) => roles.add(r))
          }
        }
      }

      // Also check standard "role" or "roles" claims
      if (Array.isArray(payload.role)) payload.role.forEach((r: string) => roles.add(r))
      else if (typeof payload.role === 'string') roles.add(payload.role)
      
      if (Array.isArray(payload.roles)) payload.roles.forEach((r: string) => roles.add(r))
      else if (typeof payload.roles === 'string') roles.add(payload.roles)

      userRoles.value = Array.from(roles)
      console.log('Detected roles:', userRoles.value)
    } catch (e) {
      console.error('Failed to parse roles', e)
      userRoles.value = []
    }
  }

  // Watch for token changes to update roles
  watch(authToken, (newToken) => {
    parseRoles(newToken)
  })

  onMounted(() => {
    if (import.meta.client) {
      const access = localStorage.getItem('auth_token')
      const refresh = localStorage.getItem('refresh_token')
      if (access) {
         authToken.value = access
         parseRoles(access)
      }
      if (refresh) refreshTokenState.value = refresh
    }
    isAuthReady.value = true
  })

  const buildAuthUrl = (state: string, codeChallenge: string) => {
    const url = new URL(`${oidc.baseUrl}/realms/${oidc.realm}/protocol/openid-connect/auth`)
    url.searchParams.set('client_id', oidc.clientId)
    url.searchParams.set('redirect_uri', oidc.redirectUri)
    url.searchParams.set('response_type', 'code')
    url.searchParams.set('scope', 'openid profile email')
    url.searchParams.set('state', state)
    url.searchParams.set('code_challenge', codeChallenge)
    url.searchParams.set('code_challenge_method', 'S256')
    return url.toString()
  }

  const sha256 = async (plaintext: string) => {
    const enc = new TextEncoder().encode(plaintext)
    const digest = await crypto.subtle.digest('SHA-256', enc)
    const bytes = Array.from(new Uint8Array(digest))
    return btoa(String.fromCharCode(...bytes))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')
  }

  const randomString = (len = 64) => {
    const arr = new Uint8Array(len)
    crypto.getRandomValues(arr)
    return Array.from(arr).map(b => ('0' + (b % 36).toString(36)).slice(-1)).join('')
  }

  const login = async () => {
    const state = randomString(16)
    const verifier = randomString(64)
    const challenge = await sha256(verifier)
    sessionStorage.setItem('pkce_state', state)
    sessionStorage.setItem('pkce_verifier', verifier)
    window.location.href = buildAuthUrl(state, challenge)
  }

  const exchangeCodeForTokens = async (code: string, state: string) => {
    const expectedState = sessionStorage.getItem('pkce_state') || ''
    if (state !== expectedState) throw new Error('Invalid state')
    const verifier = sessionStorage.getItem('pkce_verifier') || ''

    const body = new URLSearchParams()
    body.set('grant_type', 'authorization_code')
    body.set('client_id', oidc.clientId)
    body.set('code', code)
    body.set('redirect_uri', oidc.redirectUri)
    body.set('code_verifier', verifier)

    const resp = await fetch(`${oidc.baseUrl}/realms/${oidc.realm}/protocol/openid-connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
    if (!resp.ok) throw new Error('Token exchange failed')
    const json = await resp.json()
    const access = json.access_token as string
    const refresh = json.refresh_token as string
    localStorage.setItem('auth_token', access)
    localStorage.setItem('refresh_token', refresh)
    authToken.value = access
    refreshTokenState.value = refresh
  }

  const refreshToken = async () => {
    const refresh = refreshTokenState.value || localStorage.getItem('refresh_token') || ''
    if (!refresh) throw new Error('No refresh token')

    const body = new URLSearchParams()
    body.set('grant_type', 'refresh_token')
    body.set('client_id', oidc.clientId)
    body.set('refresh_token', refresh)

    const resp = await fetch(`${oidc.baseUrl}/realms/${oidc.realm}/protocol/openid-connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
    if (!resp.ok) throw new Error('Token refresh failed')
    const json = await resp.json()
    const access = json.access_token as string
    const newRefresh = json.refresh_token as string
    localStorage.setItem('auth_token', access)
    localStorage.setItem('refresh_token', newRefresh)
    authToken.value = access
    refreshTokenState.value = newRefresh
    return { access, refresh: newRefresh }
  }

  const logout = async () => {
    const refresh = refreshTokenState.value || localStorage.getItem('refresh_token') || ''
    try {
      if (refresh) {
        const body = new URLSearchParams()
        body.set('client_id', oidc.clientId)
        body.set('refresh_token', refresh)

        await fetch(`${oidc.baseUrl}/realms/${oidc.realm}/protocol/openid-connect/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        })
      }
    } catch (e) {
      console.error('Logout request failed', e)
    } finally {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      authToken.value = null
      refreshTokenState.value = null
      businessStatus.value = null
      if (import.meta.client) {
        window.location.href = '/'
      }
    }
  }

  const accountUrl = computed(() => `${oidc.baseUrl}/realms/${oidc.realm}/account`)

  return { isAuthenticated, isAuthReady, login, exchangeCodeForTokens, refreshToken, logout, accountUrl, oidc, authToken, businessStatus, userRoles, isAdmin }
}

